import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { UserService } from "@app/authentication/user.service";
import { NavigationTrackingService } from "./navigation-tracking.service";
import { routePath } from "./route-path.util";
import { TrackingDispatchService } from "./tracking-dispatch.service";
import { NavigationTriggerType } from "./tracking.models";

/**
 * Records every route change the app makes, whether or not a component called
 * NavigationTrackingService.track() for it (FLIP-212).
 *
 * Why: tracking used to be per call site, so direct URL entry, browser back/forward and any
 * un-instrumented path left no row, and "opened Score" was a floor rather than a count. That is the
 * measure Nova's "never opened Score vs opened and logged nothing" needs.
 *
 * No duplicates: an explicit track() call already says where the user is going, with an event name
 * and correlation id the router cannot know. When one exists for the same destination the router
 * row is dropped, so the explicit row is the only one. Because a component may call track() just
 * AFTER the route changes as well as before, the router row is held back for DEBOUNCE_MS before the
 * check. Each explicit call suppresses at most one router event.
 *
 * Local time needs nothing here: the JWT interceptor sends X-Local-Time on the tracking POST and the
 * API stamps NavigationEvents.InsertLocalDate from it, like every other table.
 */
@Injectable({
  providedIn: "root",
})
export class RouterNavigationTrackerService implements OnDestroy {
  static readonly EVENT_NAME = "RouteChanged";
  /** fromScreen for the first navigation of a page load: there is no previous screen. */
  static readonly ENTRY_SCREEN = "(entry)";
  static readonly DEBOUNCE_MS = 300;
  static readonly LOOKBACK_MS = 10000;

  private previousPath: string | null = null;
  private subscription: Subscription;

  constructor(
    router: Router,
    private navigationTracking: NavigationTrackingService,
    private trackingDispatch: TrackingDispatchService,
    private userService: UserService
  ) {
    this.subscription = router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.onNavigationEnd(event));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const toScreen = routePath(event.urlAfterRedirects);
    const requested = routePath(event.url);
    const fromScreen = this.previousPath ?? RouterNavigationTrackerService.ENTRY_SCREEN;
    this.previousPath = toScreen;

    // A query-string or fragment change on the same page is not a screen change.
    if (toScreen === fromScreen) {
      return;
    }

    const endedAt = Date.now();
    window.setTimeout(
      () => this.record(fromScreen, toScreen, requested, endedAt),
      RouterNavigationTrackerService.DEBOUNCE_MS
    );
  }

  private record(fromScreen: string, toScreen: string, requested: string, endedAt: number): void {
    try {
      // The tracking POST is JWT-protected and a 401 logs the user out, so there is nothing to
      // record before login or after expiry.
      if (!this.userService.getAuthToken() || this.userService.isTokenExpired()) {
        return;
      }
      if (
        this.navigationTracking.consumeExplicit(
          [toScreen, requested],
          endedAt - RouterNavigationTrackerService.LOOKBACK_MS
        )
      ) {
        return;
      }
      this.trackingDispatch.queueNavigation({
        fromScreen,
        toScreen,
        triggerType: NavigationTriggerType.RouteChange,
        eventName: RouterNavigationTrackerService.EVENT_NAME,
      });
    } catch {
      // Tracking must never break navigation.
    }
  }
}
